import { toURLSearchParams } from '@wix/sdk-runtime/rest-modules';
import { transformSDKTimestampToRESTTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { resolveUrl } from '@wix/sdk-runtime/rest-modules';
import { ResolveUrlOpts } from '@wix/sdk-runtime/rest-modules';
import { RequestOptionsFactory } from '@wix/sdk-types';

const TRANSFORM_PATHS_0 = [{ path: 'memberPrivacySettings.updatedDate' }];

function resolveComWixpressMembersMemberprivacysettingsMemberPrivacySettingsServiceUrl(
  opts: Omit<ResolveUrlOpts, 'domainToMappings'>
) {
  const domainToMappings = {
    'editor.wixapps.net': [
      {
        srcPath: '/_api/members/v1/privacy-settings',
        destPath: '/v1/privacy-settings',
      },
    ],
    'api._api_base_domain_': [
      {
        srcPath: '/members/v1/privacy-settings',
        destPath: '',
      },
    ],
    'manage._base_domain_': [
      {
        srcPath: '/_api/members/v1/privacy-settings',
        destPath: '/v1/privacy-settings',
      },
    ],
    'editor._base_domain_': [
      {
        srcPath: '/_api/members/v1/privacy-settings',
        destPath: '/v1/privacy-settings',
      },
    ],
    'blocks._base_domain_': [
      {
        srcPath: '/_api/members/v1/privacy-settings',
        destPath: '/v1/privacy-settings',
      },
    ],
    'create.editorx': [
      {
        srcPath: '/_api/members/v1/privacy-settings',
        destPath: '/v1/privacy-settings',
      },
    ],
    'www.wixapis.com': [
      {
        srcPath: '/members/v1/privacy-settings',
        destPath: '/v1/privacy-settings',
      },
    ],
    _: [
      {
        srcPath: '/_api/members/v1/privacy-settings',
        destPath: '/v1/privacy-settings',
      },
    ],
    'www._base_domain_': [
      {
        srcPath: '/_api/members/v1/privacy-settings',
        destPath: '/v1/privacy-settings',
      },
    ],
    '*.dev.wix-code.com': [
      {
        srcPath: '/_api/members/v1/privacy-settings',
        destPath: '/v1/privacy-settings',
      },
    ],
    'bo._base_domain_': [
      {
        srcPath: '/_api/members/v1/privacy-settings',
        destPath: '/v1/privacy-settings',
      },
    ],
    'wixbo.ai': [
      {
        srcPath: '/_api/members/v1/privacy-settings',
        destPath: '/v1/privacy-settings',
      },
    ],
    'wix-bo.com': [
      {
        srcPath: '/_api/members/v1/privacy-settings',
        destPath: '/v1/privacy-settings',
      },
    ],
    'apps._base_domain_': [
      {
        srcPath: '/_api/members/v1/privacy-settings',
        destPath: '/v1/privacy-settings',
      },
    ],
  };

  return resolveUrl(Object.assign(opts, { domainToMappings }));
}

const PACKAGE_NAME = '@wix/auto_sdk_members_member-privacy-settings';

/**
 * Retrieves the member privacy settings for a site.
 *
 * If no settings were set, the default settings are retrieved.
 *
 * >**Note:**
 * >This method requires [visitor or member authentication](https://dev.wix.com/docs/api-reference/articles/authentication/about-identities).
 */
export function getMemberPrivacySettings(
  payload: object
): RequestOptionsFactory<any> {
  function __getMemberPrivacySettings({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.members.v1.member_privacy_settings',
      method: 'GET' as any,
      methodFqn:
        'com.wixpress.members.memberprivacysettings.MemberPrivacySettingsService.GetMemberPrivacySettings',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressMembersMemberprivacysettingsMemberPrivacySettingsServiceUrl(
        { protoPath: '/v1/privacy-settings', data: payload, host }
      ),
      params: toURLSearchParams(payload),
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: TRANSFORM_PATHS_0,
          },
        ]),
    };

    return metadata;
  }

  return __getMemberPrivacySettings;
}

/**
 * Sets the member privacy settings for a site.
 *
 * Each time the member privacy settings are updated, `revision` increments by 1. The existing `revision` must be included when updating the member privacy settings. This ensures you're working with the latest member privacy settings and prevents unintended overwrites.
 */
export function setMemberPrivacySettings(
  payload: object
): RequestOptionsFactory<any> {
  function __setMemberPrivacySettings({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: TRANSFORM_PATHS_0,
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.members.v1.member_privacy_settings',
      method: 'POST' as any,
      methodFqn:
        'com.wixpress.members.memberprivacysettings.MemberPrivacySettingsService.SetMemberPrivacySettings',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressMembersMemberprivacysettingsMemberPrivacySettingsServiceUrl(
        { protoPath: '/v1/privacy-settings', data: serializedData, host }
      ),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: TRANSFORM_PATHS_0,
          },
        ]),
    };

    return metadata;
  }

  return __setMemberPrivacySettings;
}
